import OpenInNew from '@mui/icons-material/OpenInNew';
import { Button, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { RetainerFieldsFragment } from '~/gql/graphql';
import { CENTS_PER_DOLLAR } from '~/shared/constants';
import type { User } from '~/shared/types/connect';
import { formatCurrency } from '~/shared/utils/formatCurrency';
import {
  historyRetainerOutcomeLabel,
  retainerCardSubtitle,
  retainerCardTitle,
} from '~/modules/matters/detail/retainersTabUtils';

/** Lists closed/declined retainers from Apollo (`historyRetainers` derived in `useMatterDetailRetainers`). */
interface Props {
  historyRetainers: RetainerFieldsFragment[];
  getExpertById: (id: string) => User | undefined;
  onViewAgreement?: (requestId: string) => void;
  onViewOverview?: (requestId: string) => void;
  hideExpertName?: boolean;
}

const RetainerHistory = ({
  historyRetainers,
  getExpertById,
  onViewAgreement,
  onViewOverview,
  hideExpertName = false,
}: Props) => {
  return (
    <RetainerHistoryContainer>
      <SectionTitle variant="subtitle1">Retainer History</SectionTitle>
      {historyRetainers.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No retainer history yet.
        </Typography>
      ) : (
        <EntryList spacing={0}>
          {historyRetainers.map((retainer) => {
            const expertName = getExpertById(retainer.matterExpert.expertId)
              ?.name;
            const dateStr = new Date(retainer.updatedAt).toLocaleDateString(
              undefined,
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              },
            );
            const amountDollars = retainer.subtotal / CENTS_PER_DOLLAR;
            const hoursRaw = retainer.hoursRequested;
            const hours =
              hoursRaw !== null && hoursRaw !== undefined && hoursRaw !== ''
                ? Number.parseFloat(hoursRaw)
                : 0;
            const metaParts = [
              dateStr,
              formatCurrency(amountDollars),
              ...(Number.isFinite(hours) && hours > 0 ? [`${hours}h`] : []),
              ...(!hideExpertName && expertName ? [expertName] : []),
              historyRetainerOutcomeLabel(retainer.status),
            ];
            const historySubtitle = retainerCardSubtitle(retainer);
            return (
              <TableRow key={retainer.id}>
                <CardRow>
                  <MainContent>
                    <EntryTitle variant="body1">
                      {retainerCardTitle(retainer)}
                    </EntryTitle>
                    {historySubtitle ? (
                      <EntrySubtitle variant="body2" color="text.secondary">
                        {historySubtitle}
                      </EntrySubtitle>
                    ) : null}
                    <MetaLine variant="caption" color="text.secondary">
                      {metaParts.join(' · ')}
                    </MetaLine>
                  </MainContent>
                  <ActionsCell>
                    {onViewOverview && (
                      <ActionButton
                        size="small"
                        variant="text"
                        onClick={() => onViewOverview(retainer.id)}
                      >
                        Overview
                      </ActionButton>
                    )}
                    {onViewAgreement && (
                      <ActionButton
                        size="small"
                        variant="text"
                        onClick={() => onViewAgreement(retainer.id)}
                        endIcon={<OpenInNew fontSize="inherit" aria-hidden />}
                      >
                        View Details
                      </ActionButton>
                    )}
                  </ActionsCell>
                </CardRow>
              </TableRow>
            );
          })}
        </EntryList>
      )}
    </RetainerHistoryContainer>
  );
};

const RetainerHistoryContainer = styled(Stack)(() => ({
  width: '100%',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  fontWeight: 600,
}));

const EntryList = styled(Stack)(() => ({
  width: '100%',
}));

const TableRow = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const CardRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
}));

const MainContent = styled('div')(() => ({
  flex: 1,
  minWidth: 0,
}));

const EntryTitle = styled(Typography)(() => ({
  fontWeight: 600,
}));

const EntrySubtitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
  marginBottom: theme.spacing(0.25),
}));

const MetaLine = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

const ActionsCell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  flexShrink: 0,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

export default RetainerHistory;
